<template>
    <div id="manage-quizzes" class="relative h-full flex flex-col h-full p-4 pb-0">
        <div>
            <div class="flex justify-start items-center h-10">
                <h1 class="m-0 truncate">{{ $t('manage_school_quizzes_header') }}</h1>
            </div>
            <ManageSearch @search="onSearch($event)" :disabled-search="disableAll" class="mt-4" />
        </div>
        <div id="quizzes-section" class="relative flex-1 pb-16 overflow-auto">
            <template v-if="quizzes">
                <div class="quiz-grid">
                    <div
                        v-for="(quiz, index) in quizzes"
                        :key="index"
                        :style="`-webkit-animation-delay:${index + 1}00ms;animation-delay:${index + 1}00ms`"
                        class="quiz-item p-1 animated faster zoomIn"
                    >
                        <div class="flex flex-col w-full overflow-hidden">
                            <div
                                @click="openPreview(quiz.code)"
                                :class="{
                                    'relative quiz-image-box overflow-hidden': true,
                                    'cursor-pointer': !disableAll
                                }"
                            >
                                <img :src="imagePath(quiz)" alt="quiz-image" class="w-full h-full object-cover" />
                                <div
                                    class="title absolute flex justify-center items-center p-1 font-bold text-base z-10 text-white"
                                >
                                    <p>{{ quiz.title }}</p>
                                </div>
                                <div
                                    class="preview-box absolute pin-t pin-l flex justify-center items-center w-full h-full z-10"
                                >
                                    <i v-if="disableAll" class="fas fa-eye-slash"></i>
                                    <i v-else class="fas fa-eye"></i>
                                </div>
                                <div class="shadow absolute pin-b pin-l w-full z-0"></div>
                            </div>
                            <div class="options flex w-full">
                                <XButton
                                    v-tippy="{ placement: 'top' }"
                                    @click="openQuizDetail(quiz)"
                                    :content="$t('manage_school_quiz_tippy_quiz_detail')"
                                    :disabled="quiz.detailLoading || disableAll"
                                    bg-color="var(--ui-primary-color)"
                                    color="var(--ui-secondary-color)"
                                    class="detail-button flex-1 shadow-text-none"
                                    aria-label="Delete quiz"
                                >
                                    <span v-if="quiz.detailLoading" class="close-loader"></span>
                                    <i v-else class="fas fa-info-circle font-size-20"></i>
                                </XButton>
                                <XButton
                                    v-tippy="{ placement: 'top' }"
                                    @click="removeSchoolQuiz(quiz)"
                                    :content="$t('manage_school_quiz_tippy_remove_quiz')"
                                    :disabled="quiz.removeLoading || disableAll"
                                    bg-color="#e43146"
                                    color="var(--ui-secondary-color)"
                                    class="delete-button flex-1 shadow-text-none"
                                    aria-label="Delete quiz"
                                >
                                    <span v-if="quiz.removeLoading" class="close-loader"></span>
                                    <i v-else class="far fa-trash-alt font-size-20"></i>
                                </XButton>
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    v-if="!quizzes.length"
                    class="quiz-empty absolute pin-t pin-l flex justify-center items-center w-full h-full pb-16"
                >
                    <div class="flex flex-col justify-center items-center max-w-xl">
                        <i class="fas fa-inbox system-text-color system-text-shadow"></i>
                        <h4
                            v-html="
                                keyword
                                    ? $t('manage_school_quizzes_empty_state_keyword', { keyword })
                                    : $t('manage_school_quizzes_empty_state')
                            "
                            class="system-text-color system-text-shadow text-center m-0 mt-4"
                        ></h4>
                    </div>
                </div>
            </template>
            <InnerLoader v-else color="var(--text-color)" class="pin-t pin-l pb-16" />
        </div>
        <div v-if="quizzes && quizzes.length" class="paginator-box absolute flex justify-center items-center">
            <Paginator
                @pagination-change="onPageChange"
                :current-page="currentPage"
                :page-count="pageCount"
                :items-per-page="itemsPerPage"
                :total-items="totalItems"
            />
        </div>
        <ManageQuizDetail
            v-if="showManageQuizDetail"
            @close="closeDetailModal()"
            :quiz="quizDetail"
            class="absolute pin-t pin-l z-10"
        />
    </div>
</template>

<script>
import popup from '@/lib/popup.js'
import ManageSearch from '@/components/manage-school/ManageSearch'
import XButton from '@/components/v2/XButton'
import Paginator from '@/components/Paginator'
import InnerLoader from '@/components/InnerLoader'
import ManageQuizDetail from '@/components/manage-school/ManageQuizDetail'

export default {
    components: {
        ManageSearch,
        XButton,
        Paginator,
        InnerLoader,
        ManageQuizDetail
    },

    props: {
        registerSlug: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            currentPage: 1,
            pageCount: 0,
            itemsPerPage: 28,
            totalItems: 0,
            keyword: '',
            grade: '',
            subject: '',
            quizzes: null,
            disableAll: false,
            quizDetail: null,
            showManageQuizDetail: false
        }
    },

    created() {
        this.fetchSchoolQuizzes()
    },

    methods: {
        onSearch(keyword) {
            if (this.keyword === keyword) return

            this.fetchSchoolQuizzes(keyword)
        },

        async fetchSchoolQuizzes(keyword) {
            this.disableAll = true
            this.quizzes = null

            const urlParams = new URL(window.location.href)

            if (typeof keyword !== 'undefined') {
                this.keyword = keyword
                this.currentPage = 1

                urlParams.searchParams.delete('page')

                window.history.replaceState(null, null, urlParams.toString())
            }

            this.currentPage = urlParams.searchParams.get('page') || 1

            try {
                const res = await this.$store.dispatch('getSchoolMyQuizzes', {
                    params: {
                        keyword: this.keyword,
                        grade: this.grade,
                        subject: this.subject,
                        perpage: 28
                    },
                    slug: this.registerSlug,
                    page: this.currentPage
                })

                this.quizzes = res.data.map(quiz => {
                    return {
                        ...quiz,
                        detailLoading: false,
                        removeLoading: false
                    }
                })

                this.currentPage = res.current_page
                this.pageCount = res.page_count
                this.totalItems = res.total
            } catch (err) {
                this.toastError(this.$t('manage_school_popup_error'))
            } finally {
                this.disableAll = false
            }
        },

        imagePath(quiz) {
            return quiz.image && quiz.image.length
                ? quiz.image
                : quiz.image_placeholder && quiz.image_placeholder.length
                ? require(`../../../../public/images/${quiz.image_placeholder}`)
                : require('../../../../public/images/no-image2.png')
        },

        openPreview(code) {
            if (this.disableAll) return

            this.$emit('open-quiz-preview', code)
        },

        async openQuizDetail(quiz) {
            if (this.disableAll) return

            this.disableAll = true
            quiz.detailLoading = true

            this.quizDetail = quiz

            this.showManageQuizDetail = true

            await this.$nextTick()

            this.disableAll = false
            quiz.detailLoading = false
        },

        removeSchoolQuiz(quiz) {
            if (this.disableAll) return

            popup.confirm(this.$t('manage_school_quizzes_confirm_remove_quiz'), async () => {
                this.disableAll = true
                quiz.removeLoading = true

                try {
                    await this.$store.dispatch('removeQuizFromLibrary', {
                        quiz_id: quiz._id.$oid
                    })

                    this.toastSuccess(this.$t('manage_school_quizzes_popup_remove_quiz_complete'))

                    this.fetchSchoolQuizzes()
                } catch {
                    this.toastError(this.$t('manage_school_popup_error'))
                } finally {
                    this.disableAll = false
                }
            })
        },

        onPageChange(value) {
            this.currentPage = value

            const urlParams = new URL(window.location.href)

            urlParams.searchParams.set('page', this.currentPage)

            window.history.replaceState(null, null, urlParams.toString())

            this.fetchSchoolQuizzes()
        },

        closeDetailModal() {
            this.showManageQuizDetail = false
            this.quizDetail = null
        }
    }
}
</script>

<style lang="scss">
#manage-quizzes {
    row-gap: 1rem;

    #quizzes-section {
        .quiz-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);

            @media screen and (min-width: 640px) {
                grid-template-columns: repeat(3, 1fr);
            }

            @media screen and (min-width: 1024px) {
                grid-template-columns: repeat(4, 1fr);
            }

            .quiz-item {
                -webkit-animation-timing-function: ease;
                animation-timing-function: ease;

                > div {
                    border-radius: 0.5rem;
                    border: 1px solid var(--ui-primary-color);

                    .quiz-image-box {
                        aspect-ratio: 1 / 1;
                        .title {
                            bottom: 0;
                            left: 0;
                            width: 100%;
                            min-height: 20%;
                            background: linear-gradient(to top, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0) 100%);

                            p {
                                text-align: center;
                                overflow: hidden;
                                display: -webkit-box;
                                -webkit-box-orient: vertical;
                                -webkit-line-clamp: 2;
                                word-break: break-word;
                            }
                        }

                        .preview-box {
                            color: var(--ui-primary-color);
                            font-weight: bold;
                            font-size: 2.5rem;
                            text-shadow: 0 0 5px rgba(0, 0, 0, 0.5);
                            opacity: 0;
                            transition: opacity 0.3s;
                        }

                        .shadow {
                            min-height: 0;
                            background-image: linear-gradient(to top, rgb(0, 0, 0) 0%, rgba(0, 0, 0, 0) 100%);
                            transition: min-height 0.3s;
                        }

                        &:hover {
                            .shadow {
                                min-height: 100%;
                            }

                            .preview-box {
                                opacity: 1;
                            }
                        }
                    }

                    .options {
                        background-color: var(--ui-primary-color);

                        .toggle-switch {
                            height: 1.125rem !important;

                            .checked-label {
                                font-size: 0.625rem !important;
                            }
                        }

                        button {
                            height: 30px;
                            padding: 0;
                            border-radius: 0;

                            i {
                                font-size: 1rem;
                            }

                            &.delete-button {
                                &:hover {
                                    background-color: #c32b3d !important;
                                }
                            }
                        }
                    }
                }
            }
        }

        .quiz-empty {
            font-size: 1rem;

            @media screen and (min-width: 1200px) {
                font-size: 1.125rem;
            }

            i {
                font-size: 3rem;

                @media screen and (min-width: 1200px) {
                    font-size: 3.5rem;
                }
            }
        }
    }

    .paginator-box {
        bottom: 0;
        left: 0;
        width: 100%;
        height: 3rem;
        background-color: var(--ui-secondary-color-alpha-80);
    }

    .inner-loader {
        background-color: transparent;
    }
}
</style>
